import { axiosClient } from '../../../../library/Service/axiosClient';
import pageConfig from '../institute-admin/PageConfigInstituteGrievanceV2.json';

// API base — institute admin grievance APIs are served from school-management-service
const BASE = process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER;

export interface GrievanceTag {
    tagCode: string;
    tagLabel: string;
    tagType: 'preset' | 'system' | 'custom';
}

export interface GrievanceAlumni {
    uuid: string | null;
    name: string;
    tier: string | null;
    program: string | null;
    batch: string | null;
    avatarUrl: string | null;
    genderCode: string | null;
}

export interface GrievancePush {
    teamNotes: string;
    suggestedResolution: string;
    tags: GrievanceTag[];
}

export interface GrievanceCard {
    id: number;
    code: string;
    priority: string;
    status: string;
    category: string;
    receivedDate: string;
    updatedAt: string;
    alumni: GrievanceAlumni;
    push: GrievancePush;
}

export interface GrievanceStats {
    total: number;
    critical: number;
    pendingYourAction: number;
    resolved: number;
}

export const defaultSearchCriteria = () => ({
    search: '',
    campusId: '',
    priority: '',
    status: '',
    createdFrom: '',
    createdTo: '',
    updatedFrom: '',
    updatedTo: '',
});

class InstituteAdminGrievanceService {
    // React Router pageLoader — returns SmartContext-compatible state shape
    static async pageLoader(params: any): Promise<any> {
        const searchCriteria = defaultSearchCriteria();
        const response = await InstituteAdminGrievanceService.search(searchCriteria);

        return {
            formConfig: pageConfig,
            data: {
                searchCriteria,
                grievanceList: response.grievances ?? [],
                // AC18: stats are loaded once and stored separately from grievanceList
                stats: response.stats ?? { total: 0, critical: 0, pendingYourAction: 0, resolved: 0 },
            },
            routeInfo: { pageName: 'institute-admin-grievance-v2', ...params },
            internal: { gridState: {} },
        };
    }

    // POST /api/v1/grievance/institute-list — returns { grievances, stats }
    static async search(criteria: ReturnType<typeof defaultSearchCriteria>): Promise<{ grievances: GrievanceCard[]; stats: GrievanceStats }> {
        const response = await axiosClient().post(`${BASE}/grievance/institute-list`, criteria);
        return response.data?.data ?? { grievances: [], stats: { total: 0, critical: 0, pendingYourAction: 0, resolved: 0 } };
    }

    // POST /api/v1/grievance/:id/response — mark resolved / add response / discard
    static async submitResponse(id: number, status: string, comment: string): Promise<{ success: boolean; message: string }> {
        const response = await axiosClient().post(`${BASE}/grievance/${id}/response`, { status, comment });
        return response.data;
    }

    // AC27: Export currently shown grievances as CSV (client-side, no server call)
    static exportCSV(grievances: GrievanceCard[]): void {
        const headers = [
            'Grievance ID', 'Alumni Name', 'Program', 'Batch', 'Category',
            'Priority', 'Status', 'Received Date', 'Last Updated',
            'Team Notes', 'Suggested Resolution',
        ];

        const rows = grievances.map((g) => [
            g.code,
            g.alumni.name,
            g.alumni.program ?? '',
            g.alumni.batch ?? '',
            g.category,
            g.priority,
            g.status,
            g.receivedDate,
            g.updatedAt,
            `"${(g.push.teamNotes ?? '').replace(/"/g, '""')}"`,
            `"${(g.push.suggestedResolution ?? '').replace(/"/g, '""')}"`,
        ]);

        const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
        const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
        const filename = `grievances_export_${today}.csv`;

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}

export default InstituteAdminGrievanceService;
